import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, CodeIcon, CpuIcon, DatabaseIcon, ServerIcon, HardDriveIcon, CheckIcon } from 'lucide-react';
import { CTASection } from '../components/CTASection';
export function Services() {
  const ServiceSection = ({
    icon: Icon,
    title,
    body,
    capabilities,
    delay = 0
  }: {
    icon: any;
    title: string;
    body: string;
    capabilities: string[];
    delay?: number;
  }) => <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay
  }} className="mb-20" data-id="element-324">
      <div className="flex items-start gap-4 mb-6" data-id="element-325">
        <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-326">
          <Icon className="w-6 h-6 text-primary" data-id="element-327" />
        </div>
        <div data-id="element-328">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-329">{title}</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-330">{body}</p>

          <div className="bg-surface rounded-xl p-6" data-id="element-331">
            <h3 className="text-lg font-bold text-ink mb-4" data-id="element-332">
              Key Capabilities:
            </h3>
            <ul className="space-y-3" data-id="element-333">
              {capabilities.map((capability, index) => <li key={index} className="flex items-start" data-id="element-334">
                  <CheckIcon className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" data-id="element-335" />
                  <span className="text-gray-700" data-id="element-336">{capability}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>;
  return <div className="min-h-screen w-full bg-white" data-id="element-337">
      {/* Hero */}
      <section className="bg-gradient-to-b from-surface to-white py-20" data-id="element-338">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-340">
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6" data-id="element-341">
              Software Development Services
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-342">
              Magnus Software delivers custom software solutions across web,
              mobile, and enterprise platforms. Our engineering team builds
              scalable, maintainable systems designed for your specific business
              requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-20" data-id="element-343">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-344">
          <ServiceSection icon={SmartphoneIcon} title="Mobile Apps for iOS & Android" body="We develop native mobile applications that deliver fast, reliable experiences on both iOS and Android platforms. Whether you need a customer-facing app, internal tool, or complex enterprise mobile solution, our team handles the full development lifecycle from design through App Store deployment." capabilities={['Native iOS development (Swift, Objective-C)', 'Native Android development (Kotlin, Java)', 'Cross-platform architecture planning', 'App Store and Google Play submission', 'Push notifications and background services', 'Offline functionality and data sync']} delay={0} data-id="element-345" />

          <ServiceSection icon={CodeIcon} title="Custom Web Application Development" body="From simple business tools to complex enterprise platforms, we build web applications engineered for performance, security, and scalability. Our full-stack development expertise covers modern JavaScript frameworks, robust backend systems, and cloud infrastructure." capabilities={['Single-page applications (React, Vue, Angular)', 'RESTful and GraphQL APIs', 'Real-time features and WebSocket integration', 'Authentication and authorization systems', 'Payment processing and e-commerce', 'Progressive web apps (PWAs)']} delay={0.1} data-id="element-346" />

          <ServiceSection icon={CpuIcon} title="Embedded Systems & Industrial Software" body="We develop software for embedded systems, industrial equipment, and specialized hardware applications. Our team has experience with low-level programming, hardware interfaces, and real-time operating systems." capabilities={['Embedded Linux and RTOS development', 'Hardware interface programming', 'Industrial control systems', 'Data acquisition and monitoring', 'Protocol implementation', 'Performance optimization for constrained environments']} delay={0.2} data-id="element-347" />

          <ServiceSection icon={DatabaseIcon} title="Database Solutions & Data Migration" body="Complex data migrations, custom import/export tools, and database optimization services. We handle everything from legacy system data extraction to modern database architecture design." capabilities={['Data migration planning and execution', 'Custom ETL (Extract, Transform, Load) pipelines', 'Database schema design and optimization', 'SQL and NoSQL expertise', 'Data validation and integrity checks', 'Automated backup and recovery systems']} delay={0.3} data-id="element-348" />

          <ServiceSection icon={ServerIcon} title="High Availability Infrastructure" body="Keep your critical systems running with clustering, load balancing, and failover solutions. We design and implement infrastructure that minimizes downtime and handles traffic spikes gracefully." capabilities={['Load balancing and traffic distribution', 'Database replication and clustering', 'Automatic failover configuration', 'Health monitoring and alerting', 'Disaster recovery planning', 'Performance tuning and optimization']} delay={0.4} data-id="element-349" />

          <ServiceSection icon={HardDriveIcon} title="Global File System Implementation" body="Distributed file system solutions for organizations requiring synchronized access across multiple servers and locations. We implement and configure GFS and similar technologies for high-availability storage." capabilities={['GFS2 configuration and deployment', 'Distributed storage architecture', 'Cluster file system setup', 'Performance optimization', 'Backup and replication strategies', 'Migration from traditional storage']} delay={0.5} data-id="element-350" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-id="element-351">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-352">
          <CTASection headline="Let's Build Something Together" body="Ready to discuss your software development needs? We'd love to hear about your project." buttonText="Get in Touch" buttonLink="/contact" variant="dark" data-id="element-353" />
        </div>
      </section>
    </div>;
}